import type { AppErrorKind } from '@/lib/telemetry/faro';
import { getGraphQLErrorCode } from '@/lib/graphql-error';

/**
 * Upload error classification for telemetry — the browser half of the upload pipeline.
 *
 * Mirrors `payment-errors.ts`: a small explicit set per stage, and anything in neither set
 * is `unclassified` and STILL reported. The provider's assembly error codes ship
 * `unclassified` on purpose: nobody has measured which Transloadit / Transforge codes are
 * the user's file and which are ours, and the auth lane shipped two dead alerts in Aug 2026
 * precisely because its code sets came from docs instead of prod. Widen from data.
 *
 * Stages (one `AppFlow` each — see faro.ts):
 *   options   `getFileUploadOptions` — if THIS fails the upload dialog never opens.
 *   assembly  Uppy → Transloadit / Transforge (the bytes).
 *   save      `saveAsset` after the assembly is accepted.
 *   thumbnail the thumbnail-replace variant (its own options + assembly + save).
 */
export type UploadStage = 'options' | 'assembly' | 'save' | 'thumbnail';

export interface UploadErrorClass {
  kind: AppErrorKind;
  code: string;
}

const STAGE_FALLBACK: Record<UploadStage, string> = {
  options: 'options_query_failed',
  assembly: 'assembly_failed',
  save: 'save_failed',
  thumbnail: 'thumbnail_failed',
};

/** A GraphQL rejection the UI should have blocked (permission / limit / auth). */
const GUARD_CODES = new Set([
  'FORBIDDEN',
  'UNAUTHENTICATED',
  'PERMISSION_DENIED',
  'CREATION_LIMIT',
]);

/** Backend gate messages we know by name — kept as their own code, never as free text. */
const MESSAGE_CODES: Array<[RegExp, string]> = [
  [/S3 and DigitalOcean Spaces are disabled/i, 'storage_disabled'],
  [/Upload Files via API is not enabled/i, 'api_upload_not_enabled'],
  [/Account not found/i, 'account_missing'],
];

/** Bounded, provider-shaped code (e.g. `INPUT_FILE_EMPTY`). Anything else is dropped. */
const CODE_SHAPE = /^[A-Z0-9_]{1,64}$/;

/** The user cancelled — not a failure, never reported. */
export function isUploadCancelled(err: unknown): boolean {
  try {
    return (err as { name?: unknown } | undefined)?.name === 'AbortError';
  } catch {
    return false;
  }
}

function messageOf(err: unknown): string {
  try {
    const m = (err as { message?: unknown } | undefined)?.message;
    return typeof m === 'string' ? m : '';
  } catch {
    return '';
  }
}

/** Apollo Client 4 transport failure → `{kind: systemic, code}` or undefined. */
function networkFailure(err: unknown): UploadErrorClass | undefined {
  const ne = (err as { networkError?: { statusCode?: unknown } | null } | undefined)?.networkError;
  if (!ne) return undefined;
  const status =
    typeof ne === 'object' && typeof ne.statusCode === 'number' ? ne.statusCode : undefined;
  return { kind: 'systemic', code: status ? `http_${status}` : 'network' };
}

function classifyGraphQL(err: unknown, stage: UploadStage): UploadErrorClass | undefined {
  const net = networkFailure(err);
  if (net) return net;
  const isGql = !!err && typeof err === 'object' && 'graphQLErrors' in err;
  if (!isGql) return undefined;
  const code = getGraphQLErrorCode(err);
  if (code && GUARD_CODES.has(code)) return { kind: 'guard_regression', code };
  for (const [re, known] of MESSAGE_CODES) {
    if (re.test(messageOf(err))) return { kind: 'unclassified', code: known };
  }
  // A backend code we do not recognise: keep it (bounded) so prod tells us what it is.
  if (code && CODE_SHAPE.test(code)) return { kind: 'unclassified', code };
  return { kind: 'unclassified', code: STAGE_FALLBACK[stage] };
}

function classifyAssembly(err: unknown): UploadErrorClass {
  // Uppy's Transloadit plugin attaches the assembly to the error; the provider's own
  // error code is the only bounded thing on it.
  const assemblyErr = (err as { assembly?: { error?: unknown } } | undefined)?.assembly?.error;
  if (typeof assemblyErr === 'string' && CODE_SHAPE.test(assemblyErr)) {
    return { kind: 'unclassified', code: assemblyErr };
  }
  if (err instanceof TypeError && /fetch/i.test(messageOf(err))) {
    return { kind: 'client_network', code: 'fetch_failed' };
  }
  const status = /status (\d{3})/.exec(messageOf(err))?.[1];
  if (status)
    return { kind: status.startsWith('5') ? 'systemic' : 'unclassified', code: `http_${status}` };
  return { kind: 'unclassified', code: STAGE_FALLBACK.assembly };
}

/**
 * Classify a caught upload error for `reportAppError`. Never throws; a hostile value
 * classifies as the stage fallback.
 */
export function classifyUploadError(stage: UploadStage, err: unknown): UploadErrorClass {
  try {
    const gql = classifyGraphQL(err, stage);
    if (gql) return gql;
    if (stage === 'assembly') return classifyAssembly(err);
    if (
      stage === 'options' &&
      /Failed to get upload options|configuration unavailable/i.test(messageOf(err))
    ) {
      return { kind: 'systemic', code: 'options_empty' };
    }
    if (err instanceof TypeError && /fetch/i.test(messageOf(err))) {
      return { kind: 'client_network', code: 'fetch_failed' };
    }
    return { kind: 'unclassified', code: STAGE_FALLBACK[stage] };
  } catch {
    return { kind: 'unclassified', code: STAGE_FALLBACK[stage] };
  }
}
